import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import apiService from '../services/api';
import dayjs from 'dayjs';

interface PickChange {
  oldPick: string;
  newPick: string;
  changedAt: string;
}

interface BeamLogEntry {
  id: number;
  machineId: number;
  beamNumber: string;
  beamName?: string;
  beamTotalMeters: number;
  beamShortagePercentage: number;
  beamEffectiveMeters: number;
  beamMetersUsed: number;
  attachedAt: string;
  detachedAt?: string;
  status: 'active' | 'completed' | 'manual_detach';
  pickChanges?: PickChange[];
  machine?: { id: number; number: string };
}

// Build pick periods with duration info from pick changes
const buildPickPeriods = (log: BeamLogEntry) => {
  const changes = log.pickChanges || [];
  if (changes.length === 0) return [];

  const periods: Array<{
    pick: string;
    from: string;
    to: string | null;
    durationHours: number;
    isActive: boolean;
  }> = [];

  // First period: from beam attach to first pick change, using the oldPick of first change
  const firstChange = changes[0];
  const attachDate = dayjs(log.attachedAt);
  const firstChangeDate = dayjs(firstChange.changedAt);
  periods.push({
    pick: firstChange.oldPick,
    from: log.attachedAt,
    to: firstChange.changedAt,
    durationHours: firstChangeDate.diff(attachDate, 'hour', true),
    isActive: false,
  });

  // Middle periods: from each change to the next
  for (let i = 0; i < changes.length; i++) {
    const current = changes[i];
    const next = changes[i + 1];
    const endDate = next ? next.changedAt : (log.detachedAt || null);
    const fromDate = dayjs(current.changedAt);
    const toDate = endDate ? dayjs(endDate) : dayjs();

    periods.push({
      pick: current.newPick,
      from: current.changedAt,
      to: endDate,
      durationHours: toDate.diff(fromDate, 'hour', true),
      isActive: !endDate && log.status === 'active',
    });
  }

  return periods;
};

// Firki icon for header
const FirkiIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" />
    <line x1="12" y1="2" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="12" y1="16" x2="12" y2="22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="2" y1="12" x2="8" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="16" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="4.93" y1="4.93" x2="8.17" y2="8.17" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="15.83" y1="15.83" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="19.07" y1="4.93" x2="15.83" y2="8.17" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="8.17" y1="15.83" x2="4.93" y2="19.07" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

const BeamLog: React.FC = () => {
  const [page, setPage] = useState(1);
  const [machineFilter, setMachineFilter] = useState('');
  const [expandedPickLog, setExpandedPickLog] = useState<number | null>(null);
  const [pickDetailModal, setPickDetailModal] = useState<{ log: BeamLogEntry; periodIndex: number } | null>(null);
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['beam-logs', page, machineFilter],
    queryFn: () => {
      let url = `/beam-logs?page=${page}&limit=${limit}`;
      if (machineFilter) url += `&machineId=${machineFilter}`;
      return apiService.get(url);
    },
  });

  const beamLogs: BeamLogEntry[] = data?.beamLogs || [];
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  const { data: machinesData } = useQuery({
    queryKey: ['machines', 'water_jacket'],
    queryFn: () => apiService.get('/machines?machineType=water_jacket'),
  });
  const machines = machinesData?.machines || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>;
      case 'completed':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">Completed</span>;
      case 'manual_detach':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">Detached</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FirkiIcon className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900">Beam Log</h1>
          <span className="text-sm text-gray-500">(Last 2 months)</span>
        </div>
        <div>
          <select
            value={machineFilter}
            onChange={(e) => { setMachineFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Machines</option>
            {machines.map((m: any) => (
              <option key={m.id} value={m.id}>Machine #{m.number}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beam #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Meters</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Shortage %</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Effective</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Meters Used</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attached</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detached</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pick Changes</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-gray-500"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : beamLogs.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-400">
                    No beam logs found
                  </td>
                </tr>
              ) : (
                beamLogs.map((log) => {
                  const periods = buildPickPeriods(log);
                  const isExpanded = expandedPickLog === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono font-bold text-gray-900">
                          #{log.machine?.number || log.machineId}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.beamNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{log.beamName || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-mono text-gray-700">{log.beamTotalMeters.toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-right font-mono text-gray-700">{log.beamShortagePercentage}%</td>
                        <td className="px-4 py-3 text-sm text-right font-mono text-gray-700">{log.beamEffectiveMeters.toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-right font-mono font-bold text-gray-900">{(log.beamMetersUsed || 0).toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {dayjs(log.attachedAt).format('DD MMM YYYY')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {log.detachedAt ? dayjs(log.detachedAt).format('DD MMM YYYY') : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(log.status)}</td>
                        <td className="px-4 py-3 text-sm">
                          {log.pickChanges && log.pickChanges.length > 0 ? (
                            <button
                              onClick={() => setExpandedPickLog(isExpanded ? null : log.id)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary-50 hover:bg-primary-100 border border-primary-200 transition-colors group"
                            >
                              <span className="text-[11px] font-bold text-primary-700">
                                {log.pickChanges.length} change{log.pickChanges.length > 1 ? 's' : ''}
                              </span>
                              {isExpanded ? (
                                <ChevronUp className="w-3.5 h-3.5 text-primary-500" />
                              ) : (
                                <ChevronDown className="w-3.5 h-3.5 text-primary-500" />
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Pick Changes Timeline */}
                      {isExpanded && periods.length > 0 && (
                        <tr>
                          <td colSpan={11} className="px-0 py-0">
                            <div className="bg-gradient-to-b from-primary-50/80 to-white border-t border-b border-primary-100 px-6 py-4">
                              <div className="flex items-center gap-2 mb-3">
                                <FirkiIcon className="w-4 h-4 text-primary-500" />
                                <span className="text-xs font-bold text-primary-700 uppercase tracking-wider">
                                  Pick History — Machine #{log.machine?.number || log.machineId} — Beam {log.beamNumber}
                                </span>
                              </div>

                              {/* Timeline cards */}
                              <div className="grid gap-2">
                                {periods.map((period, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => setPickDetailModal({ log, periodIndex: idx })}
                                    className={`cursor-pointer rounded-lg border p-3 transition-all hover:shadow-md ${
                                      period.isActive
                                        ? 'bg-green-50 border-green-200 hover:border-green-300'
                                        : 'bg-white border-gray-200 hover:border-primary-300 hover:bg-primary-50/30'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        {/* Pick badge */}
                                        <div className={`flex items-center justify-center w-12 h-12 rounded-xl font-black font-mono text-lg ${
                                          period.isActive
                                            ? 'bg-green-500 text-white'
                                            : 'bg-primary-100 text-primary-700'
                                        }`}>
                                          {period.pick}
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-gray-800">Pick {period.pick}</span>
                                            {period.isActive && (
                                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase">Running</span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[11px] text-gray-500">
                                              {dayjs(period.from).format('DD MMM YYYY, hh:mm A')}
                                            </span>
                                            <span className="text-[10px] text-gray-300">&rarr;</span>
                                            <span className="text-[11px] text-gray-500">
                                              {period.to ? dayjs(period.to).format('DD MMM YYYY, hh:mm A') : 'Present'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs font-bold text-gray-500">Duration</div>
                                        <div className="text-sm font-black font-mono text-gray-800">
                                          {formatDuration(period.durationHours)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * limit) + 1} - {Math.min(pagination.page * limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pick Detail Modal */}
      {pickDetailModal && (() => {
        const { log, periodIndex } = pickDetailModal;
        const periods = buildPickPeriods(log);
        const period = periods[periodIndex];
        if (!period) return null;

        // Calculate all periods' durations for proportional meter estimation
        const totalDurationHours = periods.reduce((sum, p) => sum + p.durationHours, 0);
        const estimatedMeters = totalDurationHours > 0
          ? ((period.durationHours / totalDurationHours) * (log.beamMetersUsed || 0))
          : 0;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPickDetailModal(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Modal header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center font-black font-mono text-xl text-white">
                    {period.pick}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Pick {period.pick} Details</div>
                    <div className="text-[10px] text-primary-200">
                      Machine #{log.machine?.number || log.machineId} — Beam {log.beamNumber}
                    </div>
                  </div>
                </div>
                <button onClick={() => setPickDetailModal(null)} className="text-white/70 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body */}
              <div className="px-5 py-4 space-y-4">
                {/* Pick change info */}
                {periodIndex > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="text-[10px] font-bold text-amber-700 uppercase">Changed From</span>
                    <span className="font-mono font-bold text-amber-800">{periods[periodIndex - 1]?.pick || '-'}</span>
                    <span className="text-amber-400">&rarr;</span>
                    <span className="font-mono font-bold text-amber-800">{period.pick}</span>
                  </div>
                )}

                {/* Detail rows */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Pick Value</span>
                    <span className="text-lg font-black font-mono text-gray-900">{period.pick}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Start Date</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {dayjs(period.from).format('DD MMM YYYY, hh:mm A')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">End Date</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {period.to ? dayjs(period.to).format('DD MMM YYYY, hh:mm A') : (
                        <span className="flex items-center gap-1.5">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                          </span>
                          Running Now
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Duration</span>
                    <span className="text-sm font-bold font-mono text-gray-800">{formatDuration(period.durationHours)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-primary-50 rounded-lg px-3 -mx-1">
                    <span className="text-sm font-semibold text-primary-600">Est. Meters Produced</span>
                    <span className="text-lg font-black font-mono text-primary-700">{estimatedMeters.toFixed(1)} m</span>
                  </div>
                </div>

                {/* All pick periods summary */}
                <div className="mt-2">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">All Pick Periods</div>
                  <div className="space-y-1">
                    {periods.map((p, i) => {
                      const pMeters = totalDurationHours > 0
                        ? ((p.durationHours / totalDurationHours) * (log.beamMetersUsed || 0))
                        : 0;
                      return (
                        <div
                          key={i}
                          onClick={() => setPickDetailModal({ log, periodIndex: i })}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                            i === periodIndex
                              ? 'bg-primary-100 border border-primary-300'
                              : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold font-mono text-sm ${
                              i === periodIndex ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-700'
                            }`}>{p.pick}</span>
                            <div>
                              <span className="text-[11px] text-gray-600">{dayjs(p.from).format('DD/MM HH:mm')}</span>
                              <span className="text-[10px] text-gray-300 mx-1">&rarr;</span>
                              <span className="text-[11px] text-gray-600">{p.to ? dayjs(p.to).format('DD/MM HH:mm') : 'Now'}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[11px] font-bold font-mono text-gray-700">{pMeters.toFixed(1)}m</span>
                            <span className="text-[9px] text-gray-400 ml-1">({formatDuration(p.durationHours)})</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between items-center px-3 py-2.5 bg-gray-800 rounded-lg mt-2">
                  <span className="text-sm font-bold text-gray-300">Total Meters Used</span>
                  <span className="text-lg font-black font-mono text-white">{(log.beamMetersUsed || 0).toFixed(1)} m</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BeamLog;
